import { useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { modelFitGaps, muscleKey, type Exercise, type Program } from "@heraion/shared";

import { joinList } from "../joinList";
import { Button } from "../primitives/Button";
import { BodyweightOnlyToggle } from "../Progress/BodyweightOnlyToggle";
import type { PlanGapsLauncher } from "../Progress/planGaps";
import type { UiTheme } from "../theme";
import { useProgramSimulation } from "./useProgramSimulation";

// #492 F5: the cycle's muscles under MEV (modelFitGaps), and a button that
// hands that same fit to the host for today's draft. Nothing is written.
export function ProgramPlanGaps({
  program,
  catalog,
  onPlanGaps,
  theme,
}: {
  program: Program;
  catalog: ReadonlyArray<Exercise> | null;
  onPlanGaps: PlanGapsLauncher;
  theme: UiTheme;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  const [bodyweightOnly, setBodyweightOnly] = useState(false);
  const sim = useProgramSimulation(program, catalog);
  const fit = useMemo(
    () => (sim && catalog ? modelFitGaps(sim, catalog, { bodyweightOnly }) : null),
    [sim, catalog, bodyweightOnly],
  );
  if (!fit || fit.belowMev.length === 0) return null;
  // No candidate anywhere (e.g. bodyweight only, none for these muscles): the
  // draft would open empty.
  const nothing = fit.recommendations.every((r) => r.candidates.length === 0);
  const muscles = joinList(
    fit.belowMev.map((g) => t(muscleKey(g.muscle))),
    i18n.language,
  );
  return (
    <View style={styles.block}>
      <Text style={styles.line}>
        {t("progress.planGaps.cycleBelowMev", {
          muscles,
          defaultValue: "Below the minimum on the cycle: {{muscles}}",
        })}
      </Text>
      <BodyweightOnlyToggle
        theme={theme}
        value={bodyweightOnly}
        onValueChange={setBodyweightOnly}
        noBodyweight={fit.noBodyweight}
      />
      <Button
        theme={theme}
        variant="secondary"
        icon="target"
        label={t("progress.planGaps.button", { defaultValue: "Plan today's gaps" })}
        accessibilityHint={t("progress.planGaps.buttonHint", {
          defaultValue: "Opens today's draft in the logger. The program does not change.",
        })}
        onPress={() => onPlanGaps({ fit, bodyweightOnly })}
        disabled={nothing}
        style={styles.button}
      />
      {nothing ? (
        <Text style={styles.line}>
          {t("progress.planGaps.nothingToPlan", {
            defaultValue: "No exercise to propose for these muscles.",
          })}
        </Text>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    block: { gap: s.s2 },
    line: { color: p.ink2, fontSize: ty.sizes.base },
    button: { alignSelf: "flex-start" },
  });
}
